/**
 * UserAvatar — the signed-in user's chip in the workspace chrome.
 *
 * Visual matches `prototype/home/dashboard.html .avatar`:
 *   - Square 36×36 with `rounded-sm` corners (NOT a perfect circle — chips
 *     read as "you" in dense top-bar contexts; circles compete with the
 *     master-list record avatars below).
 *   - Brand violet background, white bold initials when no `src` is set.
 *   - When `src` is set, the image fills the square (object-cover) and the
 *     initials become the fallback if the image errors out.
 *
 * Sizes:
 *   - `sm` 28×28 — for tight spots without room for the larger chip
 *     (mobile menu drawer header, etc.).
 *   - `md` 36×36 — default; the topbar trigger size, matching prototype.
 *   - `lg` 44×44 — used inside the user-menu dropdown header.
 *   - `xl` 80×80 — profile-page cover.
 *
 * The `forwardRef` is required because Radix's DropdownMenu.Trigger uses
 * `asChild` to pass its ref through to the child element, and we want the
 * avatar to be that child rather than introducing an extra `<button>`.
 */
import * as React from "react";
import { cn } from "@flint/ui";
import { deriveInitials } from "../../../lib/format.ts";

export type UserAvatarSize = "sm" | "md" | "lg" | "xl";

export interface UserAvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Display name — "Rakesh Sharma" → "RS". */
  name: string;
  size?: UserAvatarSize;
  /**
   * Public URL to render. `null`/`undefined` shows initials. If the image
   * fails to load (404, network), the component falls back to initials so
   * we never render a broken-image icon.
   */
  src?: string | null;
}

const SIZE_CLASS: Record<UserAvatarSize, string> = {
  sm: "h-7 w-7 text-xs",
  md: "h-9 w-9 text-xs",
  lg: "h-11 w-11 text-sm",
  xl: "h-20 w-20 text-xl",
};

export const UserAvatar = React.forwardRef<HTMLDivElement, UserAvatarProps>(function UserAvatar(
  { name, size = "md", src, className, ...rest },
  ref,
) {
  // If `src` changes (avatar uploaded / cleared), reset the error gate so
  // the new URL gets a fresh shot at loading.
  const [errored, setErrored] = React.useState(false);
  React.useEffect(() => {
    setErrored(false);
  }, [src]);

  const showImage = !!src && !errored;

  return (
    <div
      ref={ref}
      aria-label={name}
      title={name}
      // `bg-slate-700` matches prototype `--violet: #334155` (the
      // misleadingly-named slate-700 token in their CSS). White ink for
      // AAA contrast at this weight — `text-white` (not `text-paper`,
      // which flips to dark slate in dark mode and fails contrast on the
      // always-dark chip). Rings on focus so the keyboard path is visible
      // when the avatar acts as a DropdownMenu trigger. When an image is
      // shown the bg is hidden behind it, but we keep the slate as a
      // backstop for transparent PNGs and slow loads.
      className={cn(
        "relative inline-grid place-items-center rounded-sm bg-slate-700 text-white font-bold tracking-tight select-none shrink-0 overflow-hidden",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-paper",
        SIZE_CLASS[size],
        className,
      )}
      {...rest}
    >
      {showImage ? (
        // `object-cover` crops to fill the square, matching the chip's
        // strong visual identity. `loading="lazy"` keeps the topbar quick
        // even when the avatar is briefly off-screen during nav.
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setErrored(true)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        deriveInitials(name)
      )}
    </div>
  );
});
